import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Dice5, Sparkles, ArrowRight, Shuffle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import NaijaAvatar from "@/components/NaijaAvatar";
import {
  SKINS, HAIR_COLORS, OUTFIT_COLORS, HAIRS, OUTFITS, ACCESSORIES, BODIES,
  DEFAULT_APPEARANCE, seededAppearance,
} from "@/lib/appearance";

const RARITY_COLOR = { common: "#A1A1AA", uncommon: "#16A34A", rare: "#FF0055", legendary: "#FFD700" };
const TABS = ["Body", "Skin", "Hair", "Outfit", "Extras"];

export default function CharacterCreate() {
  const { user, character, loadCharacter } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [city, setCity] = useState("Lagos");
  const [look, setLook] = useState(DEFAULT_APPEARANCE);
  const [tab, setTab] = useState("Body");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);

  useEffect(() => {
    if (user === false) nav("/");
    else if (character) nav("/room");
  }, [user, character, nav]);

  const set = (k, v) => setLook((p) => ({ ...p, [k]: v }));
  const randomize = () => setLook(seededAppearance(Math.random().toString(36)));

  const roll = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error("Give your Sim a street name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/character/create", { name, city, avatar: "", appearance: look });
      setReveal(data.lottery);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const enter = async () => { await loadCharacter(); nav("/room"); };

  return (
    <div className="w-full flex justify-center bg-gradient-to-b from-sky-100 to-slate-200">
      <div className="relative w-full max-w-md min-h-[100dvh] bg-[#F8FAFC] border-x border-slate-200 overflow-hidden">
        <div className="absolute -top-16 right-0 w-56 h-56 rounded-full bg-[#FFD700]/15 blur-[90px]" />

        {!reveal ? (
          <div className="relative z-10 flex flex-col min-h-[100dvh]">
            {/* ===== Live preview ===== */}
            <div className="relative px-6 pt-7 pb-3 bg-gradient-to-b from-[#16A34A]/10 to-transparent">
              <span className="label-xs text-slate-500">Step 1 · Create your Sim</span>
              <div className="relative mt-1 flex items-end justify-center h-[230px]">
                <div className="absolute bottom-3 w-40 h-5 rounded-[50%] bg-black/15 blur-[3px]" />
                <motion.div
                  key={JSON.stringify(look)}
                  initial={{ scale: 0.96, opacity: 0.6 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 180, damping: 16 }}
                  data-testid="char-preview"
                >
                  <NaijaAvatar appearance={look} view="full" size={150} />
                </motion.div>
                <button
                  type="button"
                  data-testid="randomize-btn"
                  onClick={randomize}
                  className="absolute right-0 top-0 bg-white border border-slate-200 rounded-full w-11 h-11 flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                  aria-label="Randomize look"
                >
                  <Shuffle size={18} className="text-[#16A34A]" />
                </button>
              </div>
            </div>

            {/* ===== Tabs ===== */}
            <div className="flex gap-1.5 px-4 overflow-x-auto no-scrollbar">
              {TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`tab-${t.toLowerCase()}`}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    tab === t ? "bg-[#16A34A] text-white" : "bg-white text-slate-500 border border-slate-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* ===== Controls ===== */}
            <div className="flex-1 px-5 py-4 overflow-y-auto">
              {tab === "Body" && (
                <Section label="Build">
                  <Chips options={BODIES} value={look.body} onPick={(v) => set("body", v)} testid="body" />
                </Section>
              )}

              {tab === "Skin" && (
                <Section label="Skin tone">
                  <div className="flex gap-3 flex-wrap">
                    {Object.entries(SKINS).map(([k, v]) => (
                      <Swatch key={k} color={v.fill} active={look.skin === k} onClick={() => set("skin", k)} testid={`skin-${k}`} />
                    ))}
                  </div>
                </Section>
              )}

              {tab === "Hair" && (
                <>
                  <Section label="Style">
                    <Chips options={HAIRS} value={look.hair} onPick={(v) => set("hair", v)} testid="hair" />
                  </Section>
                  <Section label="Hair colour">
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(HAIR_COLORS).map(([k, v]) => (
                        <Swatch key={k} color={v} active={look.hairColor === k} onClick={() => set("hairColor", k)} testid={`haircolor-${k}`} />
                      ))}
                    </div>
                  </Section>
                </>
              )}

              {tab === "Outfit" && (
                <>
                  <Section label="Outfit">
                    <Chips options={OUTFITS} value={look.outfit} onPick={(v) => set("outfit", v)} testid="outfit" />
                  </Section>
                  <Section label="Colour">
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(OUTFIT_COLORS).map(([k, v]) => (
                        <Swatch key={k} color={v} active={look.outfitColor === k} onClick={() => set("outfitColor", k)} testid={`outfitcolor-${k}`} />
                      ))}
                    </div>
                  </Section>
                </>
              )}

              {tab === "Extras" && (
                <Section label="Accessory">
                  <Chips options={ACCESSORIES} value={look.accessory} onPick={(v) => set("accessory", v)} testid="accessory" />
                </Section>
              )}
            </div>

            {/* ===== Name + city + roll ===== */}
            <form onSubmit={roll} className="px-5 pb-6 pt-3 bg-white border-t border-slate-200 space-y-3">
              <div className="flex gap-2">
                <input
                  data-testid="input-char-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Street name e.g. HustleKing"
                  required
                  className="flex-1 bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:border-[#16A34A] focus:outline-none"
                />
                {["Lagos", "Abuja"].map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`city-${c}`}
                    onClick={() => setCity(c)}
                    className={`px-3 rounded-xl border text-sm font-bold transition-colors ${
                      city === c ? "bg-[#16A34A] text-white border-[#16A34A]" : "bg-white text-slate-500 border-slate-200"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <button
                data-testid="roll-lottery-btn"
                disabled={busy}
                className="w-full bg-[#FFD700] text-slate-900 font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#ffe34d] transition-colors disabled:opacity-50"
              >
                <Dice5 size={20} />
                {busy ? "Rolling your destiny…" : "LOOKING SHARP — ROLL DESTINY"}
              </button>
            </form>
          </div>
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 140, damping: 12 }}
              className="relative z-10 min-h-[100dvh] flex flex-col items-center justify-center text-center px-6"
            >
              <div className="relative mb-2">
                <div className="absolute inset-x-0 -bottom-1 mx-auto w-32 h-4 rounded-[50%] bg-black/15 blur-[3px]" />
                <NaijaAvatar appearance={look} view="full" size={130} />
              </div>
              <span className="label-xs" style={{ color: RARITY_COLOR[reveal.rarity] }}>{reveal.rarity} drop</span>
              <h1 className="font-display text-4xl mt-1" style={{ color: reveal.color }}>{reveal.title}</h1>
              <p className="mt-2 text-slate-500 px-4">{reveal.tagline}</p>

              <div className="mt-5 w-full grid grid-cols-3 gap-2">
                <Stat label="Wallet" val={naira(reveal.wallet)} color="#16A34A" />
                <Stat label="Clout" val={reveal.clout} color="#FF0055" />
                <Stat label="Gems" val={reveal.gems} color="#FFD700" />
              </div>

              <div className="mt-4 w-full bg-[#FF0055]/10 border border-[#FF0055]/30 rounded-xl p-3 flex items-start gap-2 text-left">
                <Sparkles size={16} className="text-[#FF0055] mt-0.5 shrink-0" />
                <span className="text-sm text-slate-600">{reveal.perk}</span>
              </div>

              <button
                data-testid="enter-city-btn"
                onClick={enter}
                className="mt-7 w-full bg-[#16A34A] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#15933f] transition-colors"
              >
                Enter {city} <ArrowRight size={18} />
              </button>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

const Section = ({ label, children }) => (
  <div className="mb-5">
    <div className="label-xs text-slate-500 mb-2">{label}</div>
    {children}
  </div>
);

const Chips = ({ options, value, onPick, testid }) => (
  <div className="flex gap-2 flex-wrap">
    {options.map((o) => (
      <button
        key={o.id}
        type="button"
        data-testid={`${testid}-${o.id}`}
        onClick={() => onPick(o.id)}
        className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
          value === o.id ? "bg-[#16A34A] text-white border-[#16A34A] scale-105" : "bg-white text-slate-600 border-slate-200"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Swatch = ({ color, active, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-11 h-11 rounded-full border-2 transition-transform ${active ? "scale-110 ring-2 ring-offset-2 ring-[#16A34A]" : "border-white"}`}
    style={{ background: color, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
    aria-label="colour"
  />
);

const Stat = ({ label, val, color }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3">
    <div className="label-xs text-slate-500">{label}</div>
    <div className="font-display text-lg mt-1" style={{ color }}>{val}</div>
  </div>
);
